import {
  MutationFunction,
  useMutation,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query'

import { isAskedRatherThanFailed } from '@/api/extract-error-message'
import type { QueryKeyList } from '@/api/query-keys'
import { useMutationErrorSnackbar } from '@/hooks/use-mutation-error-snackbar'

/**
 * What a write already knows about its own result, written into the cache
 * before the server confirms it.
 *
 * The rows a drag left go back to where they came from if the write is
 * refused, and every surface reading that cache — not only the list the hand
 * was on — shows the new order in the meantime.
 */
export type OptimisticWrite<TVariables> = {
  /** The cached values to rewrite. The same list the write invalidates. */
  keys: QueryKeyList
  /** The cache as it reads once the write lands. Returns it unchanged where
   *  this cache does not hold what the write is about. */
  apply: (cached: unknown, variables: TVariables) => unknown
}

export type MutationCallbacks<TData, TVariables> = {
  onSuccess?: (data: TData, variables: TVariables) => void
  /** Replaces the snackbar below. React Query runs a handler passed to
   *  `mutate()` *in addition* to this one, so a call site that wants to say
   *  something else about a failure configures it here rather than there —
   *  otherwise the user gets two messages. */
  onError?: (error: unknown, variables: TVariables) => void
}

/**
 * Wraps useMutation with automatic query invalidation.
 *
 * `invalidate` is either the keys to drop or a function that drops them, given
 * what was written — a screen whose write fans out (a room touches the
 * property's aggregates as well as the room list) has that rule written once,
 * beside the query, and hands it in whole. The variables are there because how
 * far a write reaches can depend on them: turning a suggestion down touches
 * nothing but the suggestion list, while accepting it links a product.
 *
 * `invalidateOn` says when: `success` for a write whose failure changed
 * nothing, `settled` for one that may have committed part of its work — those
 * have to re-read either way, which is why six api modules were hand-rolling
 * `onSettled` around this same body.
 *
 * Without `onError` the failure is shown through `useMutationErrorSnackbar`,
 * so a coded backend error reaches the user rather than closing the form
 * silently.
 */
export function useMutationWithInvalidation<TData, TVariables = void>(
  mutationFn: MutationFunction<TData, TVariables>,
  invalidate: QueryKeyList | ((variables: TVariables) => void),
  options?: MutationCallbacks<TData, TVariables> & {
    /** What the snackbar says when the server sent no message of its own. */
    errorFallback?: string
    invalidateOn?: 'success' | 'settled'
    /** Writes the result into the cache before the server answers, and takes
     *  it back out if the answer is no. A refused write needs no refetch: what
     *  the rollback put back is what the server still holds. */
    optimistic?: OptimisticWrite<TVariables>
  },
) {
  const queryClient = useQueryClient()
  const showError = useMutationErrorSnackbar()
  const optimistic = options?.optimistic

  const run = (variables: TVariables) => {
    if (typeof invalidate === 'function') return invalidate(variables)
    invalidate.forEach(key => {
      queryClient.invalidateQueries({ queryKey: key })
    })
  }

  const settled = options?.invalidateOn === 'settled'

  return useMutation<TData, unknown, TVariables, Map<QueryKey, unknown>>({
    mutationFn,
    onMutate: optimistic
      ? async variables => {
          // Together: a refetch already on its way would land on top of what
          // is written here and put the rows back, and the request waits for
          // all of them before it goes out.
          await Promise.all(
            optimistic.keys.map(key =>
              queryClient.cancelQueries({ queryKey: key }),
            ),
          )

          const previous = new Map<QueryKey, unknown>()
          for (const key of optimistic.keys) {
            for (const [cachedKey, cached] of queryClient.getQueriesData({
              queryKey: key,
            })) {
              if (cached === undefined) continue
              // Two keys of the list may reach one cached query — a prefix and
              // the key below it. What is held for the rollback has to be the
              // cache as the write found it, not what the first pass wrote.
              if (previous.has(cachedKey)) continue
              previous.set(cachedKey, cached)
              queryClient.setQueryData(
                cachedKey,
                optimistic.apply(cached, variables),
              )
            }
          }
          return previous
        }
      : undefined,
    onSuccess: (data, variables) => {
      if (!settled) run(variables)
      options?.onSuccess?.(data, variables)
    },
    onError: (error, variables, context) => {
      for (const [key, cached] of context ?? []) {
        queryClient.setQueryData(key, cached)
      }
      // A refusal that is a question belongs to the dialog the screen opens
      // for it; reported here too it would be the same question twice, once
      // as a failure.
      if (isAskedRatherThanFailed(error)) return
      return options?.onError
        ? options.onError(error, variables)
        : showError(error, options?.errorFallback)
    },
    onSettled: settled
      ? (_data, _error, variables) => run(variables)
      : undefined,
  })
}
